/*
  As dicas da HUD (techspec-dicas; protótipo em docs/prototipos/dicas-prototipo.html): a caixa do jogo no lugar do
  `title` do navegador. Todo texto informativo de "passar o mouse" usa isto, não `title`.

  Um ouvinte só, por delegação, instalado no main.tsx: nenhum componente por botão, nenhum custo por elemento. O
  elemento só declara atributos, de preferência pelos ajudantes `dica()` e `rotulo()`:

    <button {...rotulo(texto)}>                                 uma linha (ícone, número, trava)
    <button {...rotulo(texto, 'I')}>                            uma linha com a tecla do atalho
    <button {...dica({ titulo, texto, ok, alerta, rodape })}> título, uma frase e um rodapé

  Mouse: 350 ms parado em cima; entre dicas vizinhas troca na hora. Teclado: o foco (Tab) mostra, Esc esconde.
  Toque: o toque longo (450 ms) mostra e não dispara o clique; some sozinha em 4 s. Fica acima do elemento e vira
  para baixo quando não cabe, sem sair da tela. O texto entra sempre como texto (textContent), nunca como HTML.
*/

const ATRASO_MS = 350;
const FOLGA_MS = 300;
const TOQUE_MS = 450;
const SOME_TOQUE_MS = 4000;
const MARGEM = 8;
const DIST = 10;
const SELETOR = '[data-dica],[data-dica-rotulo]';
const ID = 'dica-ativa';

/** Atributos de uma dica completa: título, uma frase e, se precisar, um exemplo e um rodapé. */
export interface DicaTexto {
  /** O nome da coisa ou a ação, em 2 ou 3 palavras. */
  titulo?: string;
  /** O efeito, numa frase só (até ~90 caracteres). */
  texto: string;
  /** Tecla do atalho, mostrada como tecla ao lado do título. */
  tecla?: string;
  /** Um exemplo quando o número sozinho não diz nada: o nome, o destaque verde e o valor. */
  exemplo?: { nome: string; destaque: string; valor: string };
  /** Rodapé verde: o que acontece (ou o que NÃO acontece) ao usar. */
  ok?: string;
  /** Rodapé vermelho: o que impede agora. */
  alerta?: string;
  /** Rodapé neutro: uma nota curta (a ordem de um ciclo, por exemplo). */
  rodape?: string;
  /** O que o clique faz, em dourado com ↗ ("Clique para ver na Wiki"). */
  acao?: string;
}

type Attrs = Record<`data-dica${string}`, string>;

/** Dica completa (o tipo "explicação" ou "ação com consequência"). */
export function dica(d: DicaTexto): Attrs {
  const a: Attrs = { 'data-dica': d.texto };
  if (d.titulo) a['data-dica-titulo'] = d.titulo;
  if (d.tecla) a['data-dica-tecla'] = d.tecla;
  if (d.exemplo) a['data-dica-exemplo'] = `${d.exemplo.nome}|${d.exemplo.destaque}|${d.exemplo.valor}`;
  if (d.ok) a['data-dica-ok'] = d.ok;
  if (d.alerta) a['data-dica-alerta'] = d.alerta;
  if (d.rodape) a['data-dica-rodape'] = d.rodape;
  if (d.acao) a['data-dica-acao'] = d.acao;
  return a;
}

/** Dica de uma linha (o tipo "rótulo"), com a tecla do atalho se tiver. */
export function rotulo(texto: string, tecla?: string): Attrs {
  return tecla ? { 'data-dica-rotulo': texto, 'data-dica-tecla': tecla } : { 'data-dica-rotulo': texto };
}

let instalado = false;
let caixa: HTMLDivElement | null = null;
let alvo: HTMLElement | null = null;
let timer = 0;
let toqueTimer = 0;
let someTimer = 0;
let ultimoFim = 0;
let engolir: HTMLElement | null = null;

function el(tag: string, cls: string, texto?: string): HTMLElement {
  const n = document.createElement(tag);
  n.className = cls;
  if (texto != null) n.textContent = texto;
  return n;
}

function montar(a: HTMLElement): HTMLDivElement {
  const d = a.dataset;
  const n = document.createElement('div');
  n.id = ID;
  n.setAttribute('role', 'tooltip');
  n.className = d.dicaRotulo ? 'dica-hud is-rotulo' : 'dica-hud';
  n.appendChild(el('i', 'dica-hud__seta'));
  const tecla = d.dicaTecla ? el('kbd', '', d.dicaTecla) : null;
  if (d.dicaRotulo) {
    n.appendChild(document.createTextNode(d.dicaRotulo));
    if (tecla) n.appendChild(tecla);
    return n;
  }
  if (d.dicaTitulo || tecla) {
    const t = el('div', 'dica-hud__t', d.dicaTitulo ?? '');
    if (tecla) t.appendChild(tecla);
    n.appendChild(t);
  }
  n.appendChild(document.createTextNode(d.dica ?? ''));
  if (d.dicaExemplo) {
    const [nome = '', destaque = '', valor = ''] = d.dicaExemplo.split('|');
    const x = el('div', 'dica-hud__x');
    const esq = el('span', '', `${nome} `);
    esq.appendChild(el('small', '', `(${destaque})`));
    x.append(esq, el('span', 'dica-hud__v', valor));
    n.appendChild(x);
  }
  if (d.dicaOk) n.appendChild(el('div', 'dica-hud__rodape is-ok', d.dicaOk));
  if (d.dicaAlerta) n.appendChild(el('div', 'dica-hud__rodape is-alerta', d.dicaAlerta));
  if (d.dicaRodape) n.appendChild(el('div', 'dica-hud__rodape', d.dicaRodape));
  if (d.dicaAcao) n.appendChild(el('div', 'dica-hud__acao', d.dicaAcao));
  return n;
}

function posicionar(n: HTMLDivElement, a: HTMLElement) {
  const r = a.getBoundingClientRect();
  const w = n.offsetWidth;
  const h = n.offsetHeight;
  let lado: 'cima' | 'baixo' = 'cima';
  let top = r.top - h - DIST;
  if (top < MARGEM) { lado = 'baixo'; top = r.bottom + DIST; }
  if (lado === 'baixo' && top + h > window.innerHeight - MARGEM) { lado = 'cima'; top = Math.max(MARGEM, r.top - h - DIST); }
  const left = Math.max(MARGEM, Math.min(r.left + r.width / 2 - w / 2, window.innerWidth - w - MARGEM));
  n.dataset.lado = lado;
  n.style.left = `${Math.round(left)}px`;
  n.style.top = `${Math.round(top)}px`;
  const seta = n.querySelector<HTMLElement>('.dica-hud__seta');
  if (seta) seta.style.left = `${Math.max(10, Math.min(w - 19, r.left + r.width / 2 - left - 4.5))}px`;
}

function mostrar(a: HTMLElement) {
  esconder(true);
  if (!a.isConnected) return;
  alvo = a;
  caixa = montar(a);
  document.body.appendChild(caixa);
  posicionar(caixa, a);
  a.setAttribute('aria-describedby', ID);
  const n = caixa;
  requestAnimationFrame(() => n.classList.add('is-on'));
}

function esconder(rapido = false) {
  window.clearTimeout(timer);
  window.clearTimeout(someTimer);
  if (alvo?.getAttribute('aria-describedby') === ID) alvo.removeAttribute('aria-describedby');
  if (caixa) {
    const n = caixa;
    if (rapido) n.remove();
    else {
      n.classList.remove('is-on');
      window.setTimeout(() => n.remove(), 150);
    }
    ultimoFim = performance.now();
  }
  caixa = null;
  alvo = null;
}

function alvoDe(t: EventTarget | null): HTMLElement | null {
  return t instanceof Element ? t.closest<HTMLElement>(SELETOR) : null;
}

/** Liga as dicas no documento inteiro. Chamar uma vez, no main.tsx. */
export function instalarDicas(): void {
  if (instalado) return;
  instalado = true;

  document.addEventListener('pointerover', e => {
    if (e.pointerType === 'touch') return;
    const a = alvoDe(e.target);
    if (!a || a === alvo) return;
    window.clearTimeout(timer);
    const pronto = !!caixa || performance.now() - ultimoFim < FOLGA_MS;
    timer = window.setTimeout(() => mostrar(a), pronto ? 0 : ATRASO_MS);
  }, { passive: true });

  document.addEventListener('pointerout', e => {
    if (e.pointerType === 'touch') return;
    const a = alvoDe(e.target);
    if (!a) return;
    if (e.relatedTarget instanceof Node && a.contains(e.relatedTarget)) return;
    window.clearTimeout(timer);
    if (a === alvo) esconder();
  }, { passive: true });

  document.addEventListener('pointerdown', e => {
    window.clearTimeout(toqueTimer);
    esconder();
    if (e.pointerType !== 'touch') return;
    const a = alvoDe(e.target);
    if (!a) return;
    toqueTimer = window.setTimeout(() => {
      mostrar(a);
      engolir = a;
      someTimer = window.setTimeout(() => esconder(), SOME_TOQUE_MS);
    }, TOQUE_MS);
  }, { passive: true });

  const soltou = () => window.clearTimeout(toqueTimer);
  document.addEventListener('pointerup', soltou, { passive: true });
  document.addEventListener('pointercancel', soltou, { passive: true });

  // O toque longo mostrou a dica: o clique que vem ao soltar não vale, nem o menu de contexto do toque longo.
  document.addEventListener('click', e => {
    if (!engolir) return;
    const a = engolir;
    engolir = null;
    if (e.target instanceof Node && a.contains(e.target)) { e.preventDefault(); e.stopPropagation(); }
  }, true);
  document.addEventListener('contextmenu', e => {
    if (engolir && e.target instanceof Node && engolir.contains(e.target)) e.preventDefault();
  }, true);

  document.addEventListener('focusin', e => {
    const a = alvoDe(e.target);
    if (a && a.matches(':focus-visible')) mostrar(a);
  });
  document.addEventListener('focusout', () => esconder());
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && caixa) esconder(); }, true);
  window.addEventListener('scroll', () => esconder(true), { capture: true, passive: true });
  window.addEventListener('resize', () => esconder(true), { passive: true });
  // O elemento saiu da tela (janela fechou) com a dica aberta: some junto.
  window.setInterval(() => { if (alvo && !alvo.isConnected) esconder(true); }, 500);
}
